import React from "react";
import { useDraggable } from "@dnd-kit/core";
import { Box, Stack, Text, UnstyledButton } from "@mantine/core";
import {
  FIELD_TYPE_GROUPS,
  FIELD_TYPE_META,
  FIELD_TYPE_ORDER,
  FieldTypeGroup,
} from "../constants";
import { FieldType } from "../../../types/formTemplates";

interface FieldSidebarProps {
  onAddField: (type: FieldType) => void;
}

const DraggableFieldButton: React.FC<{
  type: FieldType;
  onClick: () => void;
}> = ({ type, onClick }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `sidebar:${type}`,
    data: { kind: "sidebar", fieldType: type },
  });
  const meta = FIELD_TYPE_META[type];
  const Icon = meta.icon;

  return (
    <UnstyledButton
      ref={setNodeRef}
      onClick={onClick}
      {...attributes}
      {...listeners}
      aria-label={`Add ${meta.label}`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 10px",
        borderRadius: 6,
        border: "1px solid var(--mantine-color-gray-2)",
        background: isDragging
          ? "var(--mantine-color-teal-0)"
          : "var(--mantine-color-white)",
        cursor: "grab",
        fontSize: 13,
        transition: "background-color 120ms ease",
      }}
    >
      <Icon size={16} />
      <Text size="sm">{meta.label}</Text>
    </UnstyledButton>
  );
};

export const FieldSidebar: React.FC<FieldSidebarProps> = ({ onAddField }) => {
  const groupedTypes = FIELD_TYPE_GROUPS.map((g) => ({
    ...g,
    types: FIELD_TYPE_ORDER.filter(
      (t) => FIELD_TYPE_META[t].group === (g.group as FieldTypeGroup),
    ),
  }));

  return (
    <Box
      style={{
        width: 280,
        padding: 16,
        borderRight: "1px solid var(--mantine-color-gray-2)",
        background: "var(--mantine-color-white)",
        overflowY: "auto",
      }}
    >
      <Text fw={600} size="xs" c="dimmed" tt="uppercase" mb="sm">
        Form Elements
      </Text>
      <Stack gap="md">
        {groupedTypes.map((g) => (
          <div key={g.group}>
            <Text size="xs" c="dimmed" mb="xs">
              {g.label}
            </Text>
            <Stack gap="xs">
              {g.types.map((t) => (
                <DraggableFieldButton
                  key={t}
                  type={t}
                  onClick={() => onAddField(t)}
                />
              ))}
            </Stack>
          </div>
        ))}
      </Stack>
    </Box>
  );
};
